// src/services/AutoCalendarProvisioningService.js - NEW Automatic Calendar Provisioning
import { FirebaseService } from './FirebaseService';
import { GoogleCalendarAPIService } from './GoogleCalendarAPIService';


export class AutoCalendarProvisioningService {
  
  // ==========================================
  // MAIN PROVISIONING FUNCTION
  // ==========================================
  
  /**
   * Automatically provision a business calendar during login/registration
   * This makes calendar integration "just work" without manual setup
   */
    static async provisionBusinessCalendar(businessId, businessData) {
      try {
        console.log('🔧 Starting secure automatic calendar provisioning via Python backend:', businessId);
        
        // Step 1: Check if calendar already exists
        const existingIntegration = await FirebaseService.checkCalendarIntegration(businessId);
        
        if (existingIntegration.hasCalendar) {
          console.log('✅ Calendar already exists:', existingIntegration.calendarId);
          return {
            success: true,
            alreadyExists: true,
            calendarId: existingIntegration.calendarId,
            message: 'Calendar integration already active',
            calendarIntegration: existingIntegration.calendarIntegration
          };
        }

        console.log('📅 No calendar found, creating via secure Python backend...');
        
        // Step 2: Create business calendar via secure Python backend
        const calendarResult = await GoogleCalendarAPIService.initializeCalendarIntegration(
          businessId, 
          businessData
        );
        
        if (!calendarResult.success) {
          throw new Error(calendarResult.error);
        }

        // Step 3: Generate subscription URLs
        const subscriptionURLs = GoogleCalendarAPIService.generateSubscriptionURLs(
          calendarResult.calendarId, 
          businessData.business_name
        );
        
        console.log('✅ Secure business calendar provisioned successfully via Python backend');
        
        return {
          success: true,
          created: true,
          calendarId: calendarResult.calendarId,
          subscriptionURLs,
          publicUrl: calendarResult.publicUrl,
          icsUrl: calendarResult.icsUrl,
          message: 'Business calendar created and ready to use',
          setupMethod: 'secure_python_backend'
        };
        
      } catch (error) {
        console.error('❌ Secure calendar provisioning failed:', error);
        
        // Graceful degradation - mark for manual setup
        await this.markForManualSetup(businessId, error);
        
        return {
          success: false,
          error: error.message,
          requiresManualSetup: true,
          setupURL: '/dashboard/calendar-integration',
          message: 'Automatic setup failed - manual setup available'
        };
      }
    }

  // ==========================================
  // CALENDAR CREATION FUNCTIONS
  // ==========================================

  /**
   * Create a dedicated Google Calendar for the business
   */
  static async createBusinessGoogleCalendar(businessData) {
    try {
      console.log('📅 Creating Google Calendar for business:', businessData.business_name);
      
      // Prepare calendar data
      const calendarData = {
        summary: `${businessData.business_name} - Appointments`,
        description: `Professional booking calendar for ${businessData.business_name}. Automatically synchronized with booking system.`,
        timeZone: businessData.timezone || 'America/Toronto',
        location: businessData.formatted_address || '',
        selected: true
      };

      // Use existing Firebase method to create calendar
      const result = await FirebaseService.createBusinessCalendar(
        businessData.business_id || businessData.businessId,
        businessData
      );
      
      if (result.success) {
        return {
          success: true,
          calendarId: result.calendarId || result.google_calendar_id,
          calendarName: calendarData.summary,
          publicURL: result.calendarUrl || result.public_url,
          icsURL: result.icsUrl || result.ics_url
        };
      } else {
        throw new Error(result.error || 'Calendar creation failed');
      }
    } catch (error) {
      console.error('Failed to create business calendar:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Configure calendar for optimal business use
   */
  static async configureCalendarSettings(calendarId, businessData) {
    try {
      console.log('⚙️ Configuring calendar settings for business use...');
      
      // Calendar configuration would be handled via Google Calendar API
      // For now, we'll use the existing Firebase integration
      
      const settings = {
        colorId: '10', // Professional green
        timeZone: businessData.timezone || 'America/Toronto',
        defaultReminders: [
          { method: 'email', minutes: 24 * 60 }, // 24 hours
          { method: 'popup', minutes: 30 }       // 30 minutes
        ],
        visibility: 'public',
        accessRole: 'reader' // Public can see free/busy, business can edit
      };
      
      console.log('✅ Calendar configured for business use');
      return { success: true, settings };
    } catch (error) {
      console.error('Failed to configure calendar settings:', error);
      throw error;
    }
  }

  /**
   * Store calendar integration data in Firebase
   */
  static async storeCalendarIntegration(businessId, calendarResult, businessData) {
    try {
      console.log('💾 Storing calendar integration data...');
      
      const integrationData = {
        googleCalendarId: calendarResult.calendarId,
        calendar_integration: {
          google_calendar_id: calendarResult.calendarId,
          calendar_name: calendarResult.calendarName,
          public_url: calendarResult.publicURL,
          ics_url: calendarResult.icsURL,
          status: 'auto_provisioned',
          setup_method: 'automatic',
          setup_date: new Date().toISOString(),
          last_sync: new Date().toISOString(),
          created_by: 'auto_provisioning_service'
        }
      };

      // Update business profile with calendar integration
      await FirebaseService.updateBusinessProfile(businessId, integrationData);
      
      console.log('✅ Calendar integration data stored successfully');
    } catch (error) {
      console.error('Failed to store calendar integration:', error);
      throw error;
    }
  }

  /**
   * Generate universal subscription URLs for any calendar app
   */
  static async generateSubscriptionURLs(calendarId, businessData) {
    try {
      const calendarName = encodeURIComponent(`${businessData.business_name} - Appointments`);
      
      return {
        // Universal calendar subscription URLs
        googleCalendar: `https://calendar.google.com/calendar/embed?src=${calendarId}`,
        iCalURL: `https://calendar.google.com/calendar/ical/${calendarId}/public/basic.ics`,
        calDAVURL: `https://calendar.google.com/calendar/dav/${calendarId}/events`,
        
        // Platform-specific subscription links
        outlookSubscription: `https://outlook.live.com/calendar/addcalendar?name=${calendarName}&url=https://calendar.google.com/calendar/ical/${calendarId}/public/basic.ics`,
        appleCalendarURL: `webcal://calendar.google.com/calendar/ical/${calendarId}/public/basic.ics`,
        
        // Instructions for users
        instructions: {
          apple: 'Open Calendar app → File → New Calendar Subscription → Paste iCal URL',
          outlook: 'Open Outlook → Calendar → Add Calendar → Subscribe from web → Paste iCal URL',
          google: 'Already integrated - view in your Google Calendar',
          android: 'Open Google Calendar app - calendar will sync automatically',
          other: 'Use iCal URL with any calendar app that supports subscriptions'
        }
      };
    } catch (error) {
      console.error('Failed to generate subscription URLs:', error);
      return {};
    }
  }

  // ==========================================
  // ERROR HANDLING & FALLBACK
  // ==========================================

  /**
   * Mark business for manual calendar setup if auto-provisioning fails
   */
  static async markForManualSetup(businessId, error) {
    try {
      const fallbackData = {
        calendar_integration: {
          status: 'auto_setup_failed',
          error: error.message,
          manual_setup_required: true,
          last_attempt: new Date().toISOString(),
          setup_method: 'automatic_failed'
        }
      };

      await FirebaseService.updateBusinessProfile(businessId, fallbackData);
      
      console.log('📝 Business marked for manual calendar setup');
    } catch (updateError) {
      console.error('Failed to mark for manual setup:', updateError);
    }
  }


  // ==========================================
  // ENHANCED LOGIN FLOW INTEGRATION
  // ==========================================

  /**
   * Check if business needs calendar provisioning during login
   */
  static async checkProvisioningNeeded(businessId) {
    try {
      const integration = await FirebaseService.checkCalendarIntegration(businessId);
      
      return {
        needed: !integration.hasCalendar,
        reason: integration.hasCalendar ? 'already_integrated' : 'no_calendar',
        canAutoProvision: true, // Python backend handles all authentication now
        existingCalendarId: integration.calendarId
      };
      
    } catch (error) {
      console.error('Failed to check provisioning needs:', error);
      return {
        needed: true,
        reason: 'check_failed',
        canAutoProvision: false,
        error: error.message
      };
    }
  }

  /**
   * Provision during login (secure version)
   */
  static async provisionDuringLogin(userData, businessData) {
    try {
      console.log('🔐 Secure calendar provisioning during login via Python backend...');
      
      return await this.provisionBusinessCalendar(userData.businessId, businessData);
      
    } catch (error) {
      console.error('❌ Login-time provisioning failed:', error);
      return {
        success: false,
        error: error.message,
        requiresManualSetup: true
      };
    }
  }

  // ==========================================
  // PROVISIONING STATUS MANAGEMENT
  // ==========================================

  /**
   * Get current provisioning status for a business
   */
  static async getProvisioningStatus(businessId) {
    try {
      const integration = await FirebaseService.checkCalendarIntegration(businessId);
      
      if (integration.hasCalendar) {
        return {
          status: 'completed',
          method: integration.calendarIntegration?.setup_method || 'unknown',
          completedAt: integration.calendarIntegration?.setup_date,
          calendarId: integration.calendarId,
          message: 'Calendar integration active'
        };
      } else {
        const calendarIntegration = integration.calendarIntegration;
        
        if (calendarIntegration?.status === 'auto_setup_failed') {
          return {
            status: 'failed',
            method: 'automatic',
            failedAt: calendarIntegration.last_attempt,
            error: calendarIntegration.error,
            message: 'Automatic setup failed - manual setup available'
          };
        } else {
          return {
            status: 'pending',
            method: null,
            message: 'Calendar integration not set up'
          };
        }
      }
    } catch (error) {
      return {
        status: 'error',
        error: error.message,
        message: 'Unable to check provisioning status'
      };
    }
  }

  /**
   * Retry automatic provisioning (if previously failed)
   */
  static async retryProvisioning(businessId, businessData) {
    try {
      console.log('🔄 Retrying automatic calendar provisioning...');
      
      // Clear previous failure status
      await FirebaseService.updateBusinessProfile(businessId, {
        'calendar_integration.status': 'retrying',
        'calendar_integration.retry_attempt': new Date().toISOString()
      });
      
      // Attempt provisioning again
      const result = await this.provisionBusinessCalendar(businessId, businessData);
      
      return result;
    } catch (error) {
      console.error('❌ Retry provisioning failed:', error);
      return {
        success: false,
        error: error.message,
        requiresManualSetup: true
      };
    }
  }
}